import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Radio, Ship, Satellite, FlaskConical } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { TrackingTimeline, type TrackingEvent } from "./TrackingTimeline";

const NAVIERAS = [
  "Maersk", "MSC", "CMA CGM", "Hapag-Lloyd", "COSCO",
  "Evergreen", "ONE", "Yang Ming", "HMM", "ZIM",
];

const MOCK_EVENTS: TrackingEvent[] = [
  {
    date: new Date(Date.now() - 1000 * 60 * 60 * 24 * 12).toISOString(),
    location: "Shanghai, China",
    description: "Cargado en Buque – Gate In",
    status: "completed",
  },
  {
    date: new Date(Date.now() - 1000 * 60 * 60 * 24 * 7).toISOString(),
    location: "Océano Pacífico",
    description: "En Tránsito – Navegando",
    status: "completed",
  },
  {
    date: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2).toISOString(),
    location: "Balboa, Panamá",
    description: "Transbordo en Puerto intermedio",
    status: "completed",
  },
  {
    date: new Date(Date.now() + 1000 * 60 * 60 * 24 * 3).toISOString(),
    location: "Buenaventura, Colombia",
    description: "Descargado en Puerto destino (ETA)",
    status: "pending",
  },
];

interface Props {
  embarqueId: string;
  trackingNumber?: string | null;
  shippingLine?: string | null;
  trackingHistory?: TrackingEvent[] | null;
}

export function TrackingTab({ embarqueId, trackingNumber, shippingLine, trackingHistory }: Props) {
  const queryClient = useQueryClient();
  const [tracking, setTracking] = useState(trackingNumber ?? "");
  const [naviera, setNaviera] = useState(shippingLine ?? "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setTracking(trackingNumber ?? "");
    setNaviera(shippingLine ?? "");
  }, [trackingNumber, shippingLine]);

  const events: TrackingEvent[] = Array.isArray(trackingHistory) ? trackingHistory : [];

  const handleSave = async () => {
    setSaving(true);
    const { error } = await supabase
      .from("embarques")
      .update({ tracking_number: tracking || null, shipping_line: naviera || null })
      .eq("id", embarqueId);
    setSaving(false);
    if (error) {
      toast.error("Error al guardar tracking");
      return;
    }
    queryClient.invalidateQueries({ queryKey: ["embarques"] });
    toast.success("Conectando con satélite/API... (Simulación)", {
      icon: <Satellite className="h-4 w-4" />,
    });
  };

  const handleGenerateMock = async () => {
    const { error } = await supabase
      .from("embarques")
      .update({ tracking_history: MOCK_EVENTS as any })
      .eq("id", embarqueId);
    if (error) {
      toast.error("Error al generar datos de prueba");
      return;
    }
    queryClient.invalidateQueries({ queryKey: ["embarques"] });
    toast.success("Datos de prueba generados");
  };

  return (
    <div className="space-y-6">
      {/* Form */}
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
        <div className="space-y-1.5">
          <Label className="text-xs">Número de Tracking</Label>
          <Input
            placeholder="MSKU1234567 o BL#"
            value={tracking}
            onChange={(e) => setTracking(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Naviera</Label>
          <Select value={naviera} onValueChange={setNaviera}>
            <SelectTrigger>
              <SelectValue placeholder="Seleccionar naviera" />
            </SelectTrigger>
            <SelectContent>
              {NAVIERAS.map((n) => (
                <SelectItem key={n} value={n}>{n}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={handleSave} disabled={saving} className="gap-1.5">
          <Radio className="h-4 w-4" />
          {saving ? "Guardando..." : "Actualizar Rastreo"}
        </Button>
      </div>

      {/* Timeline or Empty State */}
      {events.length > 0 ? (
        <div className="rounded-lg border border-border bg-muted/20 p-4">
          <h4 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
            <Ship className="h-4 w-4 text-primary" />
            Historial de Rastreo
          </h4>
          <TrackingTimeline events={events} />
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-16 border-2 border-dashed border-border rounded-lg bg-muted/10">
          <Satellite className="h-12 w-12 text-muted-foreground/30 mb-3" />
          <p className="text-sm text-muted-foreground font-medium mb-1">Sin datos de rastreo</p>
          <p className="text-xs text-muted-foreground mb-4">
            Ingresa el número de tracking para comenzar el rastreo satelital.
          </p>
        </div>
      )}

      {/* Dev button */}
      <div className="flex justify-end">
        <Button variant="ghost" size="sm" onClick={handleGenerateMock} className="text-xs text-muted-foreground gap-1">
          <FlaskConical className="h-3 w-3" />
          Generar Datos de Prueba
        </Button>
      </div>
    </div>
  );
}
